import React, { useState } from 'react';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import StarIcon from '@mui/icons-material/Star';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import ChatIcon from '@mui/icons-material/Chat';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useAuth } from '../contexts/AuthContext';
import NotificationsList from '../components/NotificationsList';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useColorMode } from '../contexts/ColorModeContext';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

interface MainLayoutProps {
  children: React.ReactNode;
}

const DRAWER_WIDTH = 260;

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { toggleColorMode, mode } = useColorMode();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Define menu items based on role
  const getMenuItems = () => {
    const commonItems: any[] = [];

    if (!user) return [];

    if (user.role === 'Admin') {
      return [
        { text: 'Admin Dashboard', path: '/admin/dashboard', icon: <AdminPanelSettingsIcon /> },
        { text: 'Help Center', path: '/help', icon: <HelpOutlineIcon /> },
      ];
    }

    if (user.role === 'Instructor') {
      return [
        { text: 'Instructor Dashboard', path: '/instructor/dashboard', icon: <DashboardIcon /> },
        { text: 'Student Kanban', path: '/instructor/dashboard?tab=kanban', icon: <ViewWeekIcon /> },
        { text: 'Directory Profile', path: '/instructor/dashboard?tab=profile', icon: <CampaignIcon /> },
        { text: 'Expenses', path: '/instructor/dashboard?tab=expenses', icon: <ReceiptIcon /> },
        { text: 'Add Availability', path: '/instructor/availability/add', icon: <CalendarMonthIcon /> },
        { text: 'Student Management', path: '/instructor/students', icon: <PeopleIcon /> },
        { text: 'WhatsApp Connection', path: '/instructor/whatsapp', icon: <ChatIcon /> },
        { text: 'Subscription', path: '/instructor/subscribe', icon: <StarIcon /> },
        { text: 'Help Center', path: '/help', icon: <HelpOutlineIcon /> },
      ];
    }

    if (user.role === 'Student') {
      return [
        { text: 'Student Dashboard', path: '/student/dashboard', icon: <DashboardIcon /> },
        { text: 'Book a Lesson', path: '/student/book-lesson', icon: <BookOnlineIcon /> },
        { text: 'Message Instructor', path: '/student/chat', icon: <ChatIcon /> },
        { text: 'My Profile', path: '/student/profile', icon: <PersonIcon /> },
        { text: 'Help Center', path: '/help', icon: <HelpOutlineIcon /> },
      ];
    }

    return commonItems;
  };

  const menuItems = getMenuItems();

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: [1] }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ px: 2, color: 'primary.main' }}>
          Navigation Menu
        </Typography>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, px: 1 }}>
        {menuItems.map((item) => {
          const isActive = (item.path.includes('?') || location.search !== '')
            ? (location.pathname + location.search === item.path)
            : (location.pathname === item.path);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  backgroundColor: isActive ? 'primary.light' : 'transparent',
                  color: isActive ? 'primary.contrastText' : 'text.primary',
                  '& .MuiListItemIcon-root': {
                    color: isActive ? 'primary.contrastText' : 'text.secondary',
                  },
                  '&:hover': {
                    backgroundColor: isActive ? 'primary.light' : 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 'bold' : 'medium' }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <List sx={{ px: 1, pb: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              color: 'error.main',
              '& .MuiListItemIcon-root': {
                color: 'error.main',
              },
              '&:hover': {
                backgroundColor: 'error.lighter',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Log Out" primaryTypographyProps={{ fontWeight: 'medium' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: '100%',
        }}
      >
        <Toolbar>
          {isAuthenticated && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { xs: 'block', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer', flexGrow: 1 }}>
            <DirectionsCarIcon sx={{ color: 'primary.main', fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: '900', color: mode === 'dark' ? '#ffffff' : '#1a237e', letterSpacing: '-0.5px' }}>
              adi<span style={{ color: '#d32f2f' }}>L</span>essons
            </Typography>
          </Box>

          <IconButton onClick={toggleColorMode} color="inherit" sx={{ mr: 2 }}>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {isAuthenticated ? (
            <Box display="flex" alignItems="center">
              <NotificationsList />
              <Typography variant="subtitle1" sx={{ mr: 1, display: { xs: 'none', sm: 'block' }, fontWeight: 'medium' }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Chip
                label={user?.role === 'Admin' ? 'Admin' : user?.role === 'Instructor' ? 'Instructor' : 'Student'}
                size="small"
                color="secondary"
                sx={{ ml: 1, mr: 1 }}
              />
            </Box>
          ) : (
            <Box>
              <Button color="inherit" onClick={() => navigate('/login')}>
                Sign In
              </Button>
              <Button color="inherit" onClick={() => navigate('/register')} sx={{ ml: 1 }}>
                Register
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer (Sidebar) */}
      {isAuthenticated && (
        <Box component="nav">
          {/* Mobile Drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
          >
            {drawerContent}
          </Drawer>
          
          {/* Desktop Permanent Drawer */}
          <Drawer
            variant="permanent"
            open
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
          >
            {drawerContent}
          </Drawer>
        </Box>
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          mt: 8,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 2 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

// Simple helper to define sub-components color if needed
const Chip: React.FC<{ label: string; size: 'small'; color: 'secondary'; sx?: any }> = ({ label, sx }) => {
  const isInstructor = label === 'Instructor';
  const isAdmin = label === 'Admin';
  
  let bgColor = '#1976d2';
  if (isInstructor) bgColor = '#2e7d32';
  if (isAdmin) bgColor = '#9c27b0';

  return (
    <Box
      sx={{
        backgroundColor: bgColor,
        color: '#ffffff',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        px: 1.5,
        py: 0.5,
        borderRadius: 10,
        display: 'inline-block',
        ...sx
      }}
    >
      {label}
    </Box>
  );
};

export default MainLayout;
